import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import { listPublishedPosts } from "@/lib/blog.functions";

const postsQuery = queryOptions({
  queryKey: ["blog", "published"],
  queryFn: () => listPublishedPosts(),
});

export const Route = createFileRoute("/ratgeber/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(postsQuery),
  head: () => ({
    meta: [
      { title: "Ratgeber — Webdesign, SEO & digitale Strategie | PIXIWEB" },
      { name: "description", content: "Praxis-Ratgeber rund um Webdesign, SEO und digitale Strategie für KMU — verständlich erklärt, mit Checklisten und praxisnahen Tipps." },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Ratgeber — Webdesign, SEO & digitale Strategie | PIXIWEB" },
      { property: "og:description", content: "Praxis-Ratgeber rund um Webdesign, SEO und digitale Strategie für KMU — verständlich erklärt, mit Checklisten und praxisnahen Tipps." },
      { property: "og:url", content: "https://pixiweb.de/ratgeber" },
    ],
    links: [{ rel: "canonical", href: "https://pixiweb.de/ratgeber" }],
  }),
  component: RatgeberIndex,
  errorComponent: ({ error }) => (
    <div className="container-pixi py-24 text-center">
      <h1 className="font-display text-3xl font-bold text-navy">Ratgeber gerade nicht verfügbar</h1>
      <p className="mt-3 text-slate">{error.message}</p>
    </div>
  ),
  notFoundComponent: () => <div className="container-pixi py-24 text-center text-slate">Nicht gefunden.</div>,
});

function RatgeberIndex() {
  const { data } = useSuspenseQuery(postsQuery);
  const posts = data.rows;

  return (
    <div>
      <section className="border-b border-mist bg-ice">
        <div className="container-pixi py-16 md:py-24">
          <p className="eyebrow">Ratgeber</p>
          <h1 className="mt-3 font-display text-4xl font-bold text-navy md:text-5xl">
            Webdesign, SEO & digitale Strategie — verständlich erklärt
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-slate">
            Praxis-Wissen für kleine und mittelständische Unternehmen, die ihre Website ernst nehmen.
          </p>
        </div>
      </section>

      <section className="container-pixi py-16">
        {posts.length === 0 ? (
          <div className="rounded-2xl border border-mist bg-white p-12 text-center text-slate">
            Noch keine Artikel veröffentlicht — bald geht's los.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((p) => (
              <Link
                key={p.id}
                to="/ratgeber/$slug"
                params={{ slug: p.slug }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-mist bg-white transition hover:shadow-pixi"
              >
                {p.cover_image_url ? (
                  <div className="aspect-[16/9] w-full overflow-hidden bg-ice">
                    <img
                      src={p.cover_image_url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition group-hover:scale-105"
                    />
                  </div>
                ) : (
                  <div className="aspect-[16/9] w-full bg-gradient-to-br from-navy/10 to-electric/20" />
                )}
                <div className="flex flex-1 flex-col gap-3 p-5">
                  <div className="flex items-center gap-2 text-xs text-slate">
                    <span className="rounded-full bg-ice px-2 py-0.5 font-medium text-navy">{p.tag}</span>
                    <span>·</span>
                    <span>{p.reading_minutes} Min. Lesezeit</span>
                  </div>
                  <h2 className="font-display text-xl font-bold text-navy group-hover:text-electric">
                    {p.title}
                  </h2>
                  <p className="text-sm text-slate line-clamp-3">{p.excerpt}</p>
                  <div className="mt-auto pt-2 text-xs text-slate">
                    {p.published_at ? new Date(p.published_at).toLocaleDateString("de-DE", { day: "2-digit", month: "long", year: "numeric" }) : ""}
                    {" · "}
                    {p.author_name}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
